import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Popconfirm,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { useCrmActivities, useCrmActivitySummary, useCrmActivityTypes, useDeleteCrmActivity } from '@/hooks/useCrmActivities';
import { useUsers } from '@/hooks/useUsers';
import { listSalesTeams } from '@/services/salesTeam.service';
import crmActivityService, { type CrmActivity, type CrmActivityListFilters } from '@/services/crmActivity.service';
import { CrmFollowUpTag, CrmRecordLinks, crmPersonName } from '@/components/crm/activities/CrmActivityTimeline';
import { downloadXlsx } from '@/reports/utils/exportTable';
import { formatDate } from '@/utils/format/date';
import { formatNumber } from '@/utils/format/number';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

type ViewMode = 'mine' | 'team';

const DailyActivityLogPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canViewTeam = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.view_team'));
  const canCreate = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.create'));
  const canEdit = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.edit'));
  const deleteActivity = useDeleteCrmActivity();

  const [mode, setMode] = useState<ViewMode>(canViewTeam ? 'team' : 'mine');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('day'), dayjs().startOf('day')]);
  const [salesperson, setSalesperson] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [typeCodes, setTypeCodes] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [exporting, setExporting] = useState(false);

  const { data: types = [] } = useCrmActivityTypes();
  const { data: users = [] } = useUsers({ is_active: true });
  const { data: teams = [] } = useQuery({
    queryKey: ['sales-teams'],
    queryFn: () => listSalesTeams(true),
    enabled: canViewTeam,
  });

  const filters: CrmActivityListFilters = useMemo(
    () => ({
      date_from: range[0].format('YYYY-MM-DD'),
      date_to: range[1].format('YYYY-MM-DD'),
      performed_by: mode === 'mine' ? user?.id : salesperson,
      sales_team_id: mode === 'team' ? teamId : undefined,
      activity_type_codes: typeCodes.length ? typeCodes.join(',') : undefined,
      search: search.trim() || undefined,
      today: dayjs().format('YYYY-MM-DD'),
    }),
    [range, mode, user?.id, salesperson, teamId, typeCodes, search]
  );

  const { data: list, isLoading } = useCrmActivities({ ...filters, page, limit: pageSize });
  const { data: summary = [], isLoading: summaryLoading } = useCrmActivitySummary(filters);

  const totals = useMemo(
    () =>
      summary.reduce(
        (acc, s) => ({
          total: acc.total + s.total,
          contacts: acc.contacts + s.customer_contacts,
          followUps: acc.followUps + s.follow_ups_set,
        }),
        { total: 0, contacts: 0, followUps: 0 }
      ),
    [summary]
  );
  const typeName = useMemo(() => new Map(types.map((t) => [t.code, t.name])), [types]);

  const columns: ColumnsType<CrmActivity> = [
    {
      title: 'When',
      dataIndex: 'activity_date',
      width: 140,
      render: (v: string) => (
        <span>
          {formatDate(v)} <Text type="secondary">{dayjs(v).format('HH:mm')}</Text>
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'activity_type_name',
      width: 120,
      render: (v: string) => <Tag color="geekblue">{v}</Tag>,
    },
    ...(mode === 'team'
      ? [
          {
            title: 'Salesperson',
            key: 'performer',
            width: 150,
            render: (_: unknown, a: CrmActivity) => crmPersonName(a.performer),
          },
        ]
      : []),
    {
      title: 'Customer / record',
      key: 'record',
      width: 220,
      render: (_: unknown, a: CrmActivity) => <CrmRecordLinks a={a} />,
    },
    {
      title: 'Discussion',
      key: 'discussion',
      render: (_: unknown, a: CrmActivity) => (
        <div>
          {a.subject && <Text strong>{a.subject}</Text>}
          {a.contact_name && <Text type="secondary"> · with {a.contact_name}</Text>}
          {a.description && (
            <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }} ellipsis={{ rows: 2, expandable: true }}>
              {a.description}
            </Paragraph>
          )}
          {a.outcome && (
            <div>
              <Text type="secondary">Outcome: </Text>
              {a.outcome}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Follow-up',
      key: 'follow_up',
      width: 190,
      render: (_: unknown, a: CrmActivity) => <CrmFollowUpTag a={a} />,
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_: unknown, a: CrmActivity) => {
        if (!canEdit || a.activity_type_code === 'SYSTEM') return null;
        return (
          <Space size={0}>
            <Button type="link" size="small" onClick={() => navigate(`/growth/activities/${a.id}/edit`)}>
              Edit
            </Button>
            <Popconfirm title="Delete this activity?" onConfirm={() => deleteActivity.mutate(a)}>
              <Button type="link" size="small" danger>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const all = await crmActivityService.list({ ...filters, page: 1, limit: 500 });
      const rows = all.items.map((a) => ({
        date: formatDate(a.activity_date),
        time: dayjs(a.activity_date).format('HH:mm'),
        type: a.activity_type_name,
        salesperson: crmPersonName(a.performer),
        customer: a.customer?.customer_name || a.opportunity?.customer_name || a.lead?.customer_name || '',
        record: a.opportunity?.opportunity_number || a.lead?.lead_number || '',
        contact: a.contact_name ?? '',
        subject: a.subject ?? '',
        notes: a.description ?? '',
        requirements: a.customer_requirements ?? '',
        outcome: a.outcome ?? '',
        sentiment: a.sentiment ?? '',
        follow_up_date: a.follow_up_date ? formatDate(a.follow_up_date) : '',
        follow_up_status: a.follow_up_status ?? '',
        commitment: a.is_commitment ? a.commitment_text ?? '' : '',
      }));
      const cols = [
        ['date', 'Date'],
        ['time', 'Time'],
        ['type', 'Type'],
        ['salesperson', 'Salesperson'],
        ['customer', 'Customer'],
        ['record', 'Lead / opportunity'],
        ['contact', 'Contact'],
        ['subject', 'Subject'],
        ['notes', 'Notes'],
        ['requirements', 'Customer requirements'],
        ['outcome', 'Outcome'],
        ['sentiment', 'Sentiment'],
        ['follow_up_date', 'Follow-up date'],
        ['follow_up_status', 'Follow-up status'],
        ['commitment', 'Commitment'],
      ].map(([key, title]) => ({ key, title, dataIndex: key }));
      const label =
        filters.date_from === filters.date_to ? filters.date_from : `${filters.date_from}_to_${filters.date_to}`;
      await downloadXlsx(`sales-activity-log-${label}`, cols, rows);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          Daily Sales Activity Log
        </Title>
        <Space wrap>
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void exportXlsx()}>
            Export XLSX
          </Button>
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/activities/new')}>
              Log activity
            </Button>
          )}
        </Space>
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          {canViewTeam && (
            <Segmented<ViewMode>
              value={mode}
              onChange={(v) => {
                setMode(v);
                setPage(1);
              }}
              options={[
                { label: 'My day', value: 'mine' },
                { label: 'Team', value: 'team' },
              ]}
            />
          )}
          <Button
            onClick={() => {
              setRange([dayjs().startOf('day'), dayjs().startOf('day')]);
              setPage(1);
            }}
          >
            Today
          </Button>
          <Button
            onClick={() => {
              const today = dayjs().startOf('day');
              setRange([today.subtract((today.day() + 6) % 7, 'day'), today]);
              setPage(1);
            }}
          >
            This week
          </Button>
          <RangePicker
            value={range}
            format="DD-MM-YYYY"
            allowClear={false}
            onChange={(v) => {
              if (v?.[0] && v?.[1]) {
                setRange([v[0], v[1]]);
                setPage(1);
              }
            }}
          />
          {mode === 'team' && (
            <>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Salesperson"
                style={{ width: 200 }}
                value={salesperson}
                onChange={(v) => {
                  setSalesperson(v);
                  setPage(1);
                }}
                options={users.map((u) => ({
                  value: u.id,
                  label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
                }))}
              />
              <Select
                allowClear
                placeholder="Sales team"
                style={{ width: 180 }}
                value={teamId}
                onChange={(v) => {
                  setTeamId(v);
                  setPage(1);
                }}
                options={teams.map((t) => ({ value: t.id, label: t.team_name }))}
              />
            </>
          )}
          <Select
            mode="multiple"
            allowClear
            placeholder="Activity types"
            style={{ minWidth: 200 }}
            value={typeCodes}
            onChange={(v) => {
              setTypeCodes(v);
              setPage(1);
            }}
            options={types.map((t) => ({ value: t.code, label: t.name }))}
          />
          <Input.Search
            allowClear
            placeholder="Search notes, subject, contact"
            style={{ width: 240 }}
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
          />
        </Space>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" loading={summaryLoading}>
            <Statistic title="Activities" value={formatNumber(totals.total, { maximumFractionDigits: 0 })} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" loading={summaryLoading}>
            <Statistic title="Customer contacts" value={formatNumber(totals.contacts, { maximumFractionDigits: 0 })} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" loading={summaryLoading}>
            <Statistic title="Follow-ups set" value={formatNumber(totals.followUps, { maximumFractionDigits: 0 })} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" loading={summaryLoading}>
            <Statistic title="Salespeople active" value={summary.length} />
          </Card>
        </Col>
      </Row>

      {mode === 'team' && (
        <Card size="small" title="By salesperson" style={{ marginBottom: 16 }} loading={summaryLoading}>
          {summary.length ? (
            <Table
              size="small"
              rowKey="user_id"
              pagination={false}
              dataSource={summary}
              onRow={(r) => ({
                onClick: () => {
                  setSalesperson(r.user_id);
                  setPage(1);
                },
                style: { cursor: 'pointer' },
              })}
              columns={[
                { title: 'Salesperson', dataIndex: 'user_name' },
                { title: 'Activities', dataIndex: 'total', align: 'right', width: 100 },
                { title: 'Customer contacts', dataIndex: 'customer_contacts', align: 'right', width: 140 },
                { title: 'Follow-ups set', dataIndex: 'follow_ups_set', align: 'right', width: 120 },
                {
                  title: 'By type',
                  key: 'by_type',
                  render: (_: unknown, r) => (
                    <Space size={4} wrap>
                      {Object.entries(r.by_type).map(([code, n]) => (
                        <Tag key={code}>
                          {typeName.get(code) ?? code}: {n}
                        </Tag>
                      ))}
                    </Space>
                  ),
                },
              ]}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity logged in this period" />
          )}
        </Card>
      )}

      <Card size="small">
        <Table<CrmActivity>
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={list?.items ?? []}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total: list?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
          locale={{ emptyText: <Empty description="No activities in this period" /> }}
        />
      </Card>
    </div>
  );
};

export default DailyActivityLogPage;
